import { useEffect, useMemo } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  getListOccurrencesQueryKey,
  listOccurrences,
} from '../../api/generated/scheduling/scheduling';
import type { ClassOccurrence, Me } from '../../api/generated/model';
import { formatDateInPersonTimezone, formatTimeInPersonTimezone } from '../../i18n/datetime';
import styles from './dashboard.module.css';

/**
 * Janela que a home consulta: de agora ate duas semanas a frente.
 *
 * Duas semanas, e nao um dia: quem tem uma aula por semana veria a home dizer
 * "nada precisa da sua atencao" na segunda-feira por causa de uma janela curta,
 * que e exatamente o defeito que esta home tinha. Quem quer o mes inteiro tem a
 * agenda, que e outra tela e ja traz os modos de visualizacao.
 */
export const HOME_WINDOW_DAYS = 14;

export function homeWindow(now: Date = new Date()): { from: string; to: string } {
  const to = new Date(now);
  to.setDate(to.getDate() + HOME_WINDOW_DAYS);
  return { from: now.toISOString(), to: to.toISOString() };
}

/**
 * Estados em que a aula ainda vai acontecer.
 *
 * O que foi desmarcado, anulado ou ja terminou nao e proxima acao de ninguem: o
 * horario liberado interessa na agenda, onde ha o que fazer com ele, e nao na
 * home. O conjunto e fechado sobre o enum do contrato, entao um estado novo
 * quebra a compilacao em vez de escorregar para dentro da lista sem ninguem ver.
 */
const STILL_HAPPENING: ReadonlySet<ClassOccurrence['status']> = new Set<ClassOccurrence['status']>([
  'scheduled',
  'open',
  'in_progress',
]);

/**
 * As aulas que ainda vao acontecer, da mais proxima para a mais distante.
 *
 * A ordenacao compara **instantes**, e nao as strings: o contrato promete um
 * instante UTC em `startsAtUtc`, e nao um unico jeito de escreve-lo.
 */
export function upcoming(items: readonly ClassOccurrence[]): ClassOccurrence[] {
  return items
    .filter((occ) => STILL_HAPPENING.has(occ.status))
    .sort((a, b) => Date.parse(a.startsAtUtc) - Date.parse(b.startsAtUtc));
}

export function nextUpcoming(items: readonly ClassOccurrence[]): ClassOccurrence | undefined {
  return upcoming(items)[0];
}

/**
 * O teto do contrato para `limit` (`maximum: 200`), honrado por
 * `seriesPageSize` (`api/internal/scheduling/series.go`) sem reduzir.
 */
const OCCURRENCES_PAGE_SIZE = 200;

/** A janela da home, e o que as duas homes podem afirmar sobre ela. */
export interface HomeOccurrences {
  readonly items: readonly ClassOccurrence[];
  /**
   * Verdadeiro so quando o cursor acabou. Ate la, `items` e um PREFIXO da
   * janela — e as duas afirmacoes que as homes fazem sobre ela ("nada precisa
   * da sua atencao agora" e "a quinzena inteira") sao afirmacoes sobre a
   * janela toda.
   */
  readonly complete: boolean;
  readonly failed: boolean;
}

/**
 * A JANELA INTEIRA da home, por seguir o cursor ate `nextCursor` nulo.
 *
 * `listOccurrences` responde no maximo `limit` por chamada — 50 por default no
 * servidor — e devolve `nextCursor`; as duas homes pediam sem cursor. Duas
 * afirmacoes saiam disso, e as duas mentiam para um caso que existe:
 *
 * 1. `ClassList` diz, na propria docstring, ser "a janela inteira, em lista".
 *    Um professor com cinco aulas por dia passa de 50 ocorrencias em quinze
 *    dias, e a quinzena dele aparecia cortada sem nenhuma linha dizendo que
 *    havia mais.
 * 2. "Nada precisa da sua atencao agora" e uma afirmacao sobre o servidor (ver
 *    `PanelSection`). A lista vem ordenada por `starts_at_utc`, entao a mais
 *    proxima esta na primeira pagina — mas `nextUpcoming` filtra pelos estados
 *    em que a aula ainda vai acontecer: com a primeira pagina inteira
 *    desmarcada, a aula que sobra fica atras do cursor, e a home anuncia
 *    tranquilidade a quem tem aula marcada. E o desfecho exato que a docstring
 *    de `PanelSection` conta como ja tendo acontecido uma vez.
 *
 * A janela nasce uma vez por montagem: recalcula-la a cada render trocaria a
 * chave da consulta a cada milissegundo, e a home nunca sairia do carregando.
 */
export function useHomeOccurrences(enabled: boolean): HomeOccurrences {
  const range = useMemo(() => homeWindow(), []);
  const query = useInfiniteQuery({
    queryKey: getListOccurrencesQueryKey({ ...range, limit: OCCURRENCES_PAGE_SIZE }),
    queryFn: ({ pageParam, signal }) =>
      listOccurrences(
        pageParam === undefined
          ? { ...range, limit: OCCURRENCES_PAGE_SIZE }
          : { ...range, limit: OCCURRENCES_PAGE_SIZE, cursor: pageParam },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });

  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = query;
  useEffect(() => {
    // `isFetchNextPageError` faz parte da condicao de parada: sem ele, a pagina
    // que falha no meio deixa `hasNextPage` verdadeiro e `isFetchingNextPage` de
    // volta em falso, o efeito dispara de novo e a home entra em tempestade de
    // requisicoes contra o servidor que acabou de recusar.
    if (!hasNextPage || isFetchingNextPage || isFetchNextPageError) return;
    void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  return {
    items: (query.data?.pages ?? []).flatMap((page) => page.items),
    complete: query.data !== undefined && !hasNextPage,
    failed: query.isError || isFetchNextPageError,
  };
}

/** O recorte que `panelState` consome, montado a partir da janela completa. */
export function occurrencesPanelQuery(
  occurrences: HomeOccurrences,
): { readonly isError: boolean; readonly data: readonly ClassOccurrence[] | undefined } {
  return {
    isError: occurrences.failed,
    data: occurrences.complete ? occurrences.items : undefined,
  };
}

/**
 * Quando a aula comeca, **no fuso da pessoa**.
 *
 * O contrato guarda o instante em UTC e a §2.2 manda exibir no fuso de quem
 * olha, que chega em `GET /v1/me`. Uma aula das 23h em Nova York apareceria no
 * dia seguinte para quem abrisse a home em Sao Paulo.
 */
function startsAt(occ: ClassOccurrence, me: Me): string {
  const date = formatDateInPersonTimezone(occ.startsAtUtc, me.timezone, me.locale);
  const time = formatTimeInPersonTimezone(occ.startsAtUtc, me.timezone, me.locale);
  return `${date} ${time}`;
}

/**
 * A aula mais proxima, em destaque.
 *
 * **Nenhum nome de pessoa entra aqui, e isso vale para os dois papeis.** Nao e
 * economia de espaco: com o nome mascarado dois alunos aparecem como `João S.`
 * sem desambiguador, e a home do professor viraria a lista de pessoas que a §9.3
 * recusa. A aula se identifica pelo horario, que e como o professor age.
 */
export function NextClass({ me, occ }: { readonly me: Me; readonly occ: ClassOccurrence }) {
  const { t } = useTranslation();

  return (
    <p className={styles.hero}>
      <span className={styles.heroValue}>{startsAt(occ, me)}</span>
      <span className={styles.tileLabel}>
        {t(`schedule.kind.${occ.kind}`)} · {t(`schedule.status.${occ.status}`)}
      </span>
    </p>
  );
}

/**
 * A janela inteira, em lista.
 *
 * A primeira linha repete a aula em destaque de proposito: o destaque responde
 * "o que eu faco agora" e a lista responde "como esta a minha quinzena". Cortar
 * a primeira linha daqui deixaria a lista dizendo "nenhuma aula neste periodo"
 * para quem tem exatamente uma — que e a mesma mentira, so que menor.
 */
export function ClassList({
  me,
  items,
}: {
  readonly me: Me;
  readonly items: readonly ClassOccurrence[];
}) {
  const { t } = useTranslation();

  return (
    <ul className={styles.list}>
      {items.map((occ) => (
        <li key={occ.id} className={styles.listItem}>
          <span>{startsAt(occ, me)}</span>
          <span className={styles.role}>
            {t(`schedule.kind.${occ.kind}`)} · {t(`schedule.status.${occ.status}`)}
          </span>
        </li>
      ))}
    </ul>
  );
}
